import { type DependencyList, useEffect, useRef, useState } from 'react';

import { isServerSide } from '@/helpers/isServerSide';
import { log } from '@/helpers/util/logger';

type UseLazyLoadResourcesLoadFn = (...args: any[]) => void | Promise<void>;

type UseLazyLoadResourcesOptions = {
  deps?: DependencyList;
  isEnabled?: boolean;
  once?: boolean;
};

export const useLazyLoadResources = (loadFn: UseLazyLoadResourcesLoadFn, options: UseLazyLoadResourcesOptions = {}) => {
  const { deps = [], isEnabled = true, once = true } = options;

  const [isReady, setIsReady] = useState(() => {
    if (isServerSide()) return false;
    return document.readyState === 'complete';
  });

  const wasLoaded = useRef(false);

  const fnRef = useRef<UseLazyLoadResourcesLoadFn>(loadFn);

  useEffect(() => {
    fnRef.current = loadFn;
  }, [loadFn]);

  useEffect(() => {
    if (isServerSide()) return;
    if (isReady) return;

    if (document.readyState === 'complete') {
      setIsReady(true);
      return;
    }

    const onLoad = () => {
      setIsReady(true);
    };

    window.addEventListener('load', onLoad, { capture: true, once: true, passive: true });
    return () => window.removeEventListener('load', onLoad);
  }, [isReady]);

  useEffect(() => {
    if (isServerSide()) return;
    if (!isEnabled) return;
    if (!isReady) return;
    if (wasLoaded.current && once) return;

    wasLoaded.current = true;

    const run = () => {
      const errorHandler = (error: Error) => {
        const errorMessage = `Error loading third party resources: ${error.message}`;
        console.error(errorMessage);
        log(errorMessage);
      };

      try {
        const maybePromise = fnRef.current();
        if (maybePromise instanceof Promise) {
          maybePromise.catch(errorHandler);
        }
      } catch (error) {
        errorHandler(error as Error);
      }
    };

    setTimeout(run, 0);

    return () => {};
  }, [isReady, once, isEnabled, ...deps]);

  return isReady;
};

export default useLazyLoadResources;
